import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
  isAdmin,
} from "../../components/utils";
import { Input, TextArea } from "../../components/shared/InputField";
import Tooltip from "../../components/shared/Tooltip";
import {
  useGetGrantApprovalStatus,
  useSubmitSignOff,
  useSubmitRecommendChange,
  useOverrideSignOff,
  useGetGrantRecommendations,
} from "../../queries/grantApproval";
import {
  GrantApproval,
  GrantApprovalStatus,
  SignOffDecision,
  deriveFlowStatus,
} from "../../types/GrantApproval";
import { GrantApprovalConfig } from "../../types/EsopPlan";
import { formatDisplayDate } from "../../utils/date";
import { RecommendationInlineActions } from "../grants/RecommendedChangesAdmin/RecommendationsList";

type Props = {
  grantId: string;
  isCurrentUserApprover: boolean;
  currentUserId?: string;
  planApprovalConfig?: GrantApprovalConfig;
  defaultExpanded?: boolean;
  initialActionMode?: "none" | "recommend_change";
  recommendationInputMode?: "OPTIONS" | "VALUE";
  onProposedChangePreview?: (changes: Record<string, string>) => void;
  onClose?: () => void;
};

const STATUS_CONFIG: Record<
  GrantApprovalStatus,
  { label: string; className: string }
> = {
  PENDING: { label: "Pending", className: "bg-gray-100 text-gray-500" },
  APPROVED: { label: "Approved", className: "bg-green-50 text-green-600" },
  REJECTED: { label: "Rejected", className: "bg-red-50 text-red-600" },
  EXPIRED: { label: "Expired", className: "bg-amber-50 text-amber-600" },
  RECOMMEND_CHANGE: {
    label: "Change Recommended",
    className: "bg-orange-50 text-orange-600",
  },
};

// Fields that are realistically editable via a recommend-change proposal.
const PROPOSED_CHANGE_FIELDS: {
  key: string;
  label: string;
  inputType: "number" | "date";
}[] = [{ key: "optionsGranted", label: "No of Options", inputType: "number" }];

type ProposedChangeRow = { field: string; value: string };

function timingText(row: GrantApproval): string {
  if (row.status === "APPROVED" && row.metaData?.respondedAt)
    return `Approved on ${formatDisplayDate(
      new Date(row.metaData.respondedAt).toUTCString()
    )}`;
  if (row.status === "REJECTED" && row.metaData?.respondedAt)
    return `Rejected on ${formatDisplayDate(
      new Date(row.metaData.respondedAt).toUTCString()
    )}`;
  if (row.status === "PENDING" && row.metaData?.notifiedAt) {
    let text = row.metaData?.expiresAt
      ? `Expires ${formatDisplayDate(
          new Date(row.metaData.expiresAt).toUTCString()
        )}`
      : "Notified — awaiting response";
    if (row.metaData?.onExpiryAction) {
      const actionLabel =
        row.metaData.onExpiryAction === "AUTO_APPROVE"
          ? "will auto-approve"
          : row.metaData.onExpiryAction === "AUTO_REJECT"
          ? "will auto-reject"
          : "will escalate to admin";
      text += ` (${actionLabel} on expiry)`;
    }
    return text;
  }
  if (row.status === "EXPIRED")
    return row.metaData?.autoActed ? "Auto-acted on expiry" : "Window expired";
  return "Waiting — not yet notified";
}

export function GrantApprovalCard({
  grantId,
  isCurrentUserApprover,
  currentUserId,
  planApprovalConfig,
  defaultExpanded = false,
  initialActionMode = "none",
  recommendationInputMode = "OPTIONS",
  onProposedChangePreview,
  onClose,
}: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [comment, setComment] = useState("");
  const [actionMode, setActionMode] = useState<"none" | "recommend_change">(
    initialActionMode
  );
  const [proposedChangeRows, setProposedChangeRows] = useState<
    ProposedChangeRow[]
  >([{ field: "", value: "" }]);
  const [proposedInputMode, setProposedInputMode] = useState<
    "OPTIONS" | "VALUE"
  >(recommendationInputMode);

  useEffect(() => {
    setProposedInputMode(recommendationInputMode);
  }, [recommendationInputMode]);

  useEffect(() => {
    if (!onProposedChangePreview) return;
    const changes = proposedChangeRows.reduce<Record<string, string>>(
      (acc, row) => {
        if (row.field && row.value.trim()) acc[row.field] = row.value;
        return acc;
      },
      {}
    );
    onProposedChangePreview(changes);
  }, [proposedChangeRows]);

  const [overrideRowId, setOverrideRowId] = useState<string | null>(null);
  const [overrideAction, setOverrideAction] = useState<"approved" | "rejected">(
    "approved"
  );
  const [overrideComment, setOverrideComment] = useState("");

  const { data: liveRows, isLoading: fetchingRows } =
    useGetGrantApprovalStatus(grantId);
  const { data: recommendationsRes } = useGetGrantRecommendations(grantId);
  const recommendationByApprovalId = new Map(
    (recommendationsRes?.recommendations ?? []).map((r) => [
      r.grantApprovalId,
      r,
    ])
  );

  const { mutate: submitSignOff, isLoading: signOffLoading } =
    useSubmitSignOff();
  const { mutate: submitRecommendChange, isLoading: recommendLoading } =
    useSubmitRecommendChange();
  const { mutate: overrideSignOff, isLoading: overrideLoading } =
    useOverrideSignOff();
  const userIsAdmin = isAdmin();

  const rows: GrantApproval[] =
    liveRows ??
    (planApprovalConfig?.approvers ?? []).map((a) => ({
      id: `plan-${a.userId}`,
      grantId,
      userId: a.userId,
      approverName: a.name,
      order: a.order,
      status: "PENDING" as GrantApprovalStatus,
      metaData: {
        onExpiryAction: a.onExpiryAction,
      },
    }));

  if (!fetchingRows && rows.length === 0) return null;

  const minApprovers = planApprovalConfig?.minApprovers;
  const approvalOrder = planApprovalConfig?.approvalOrder;
  // Keep this aligned with the backend: only NO_ORDER means parallel. Older
  // or partially populated plan responses should retain the default flow.
  const isSequential = approvalOrder !== "NO_ORDER";
  const flowStatus = deriveFlowStatus(liveRows, minApprovers);
  const approvedCount = rows.filter((r) => r.status === "APPROVED").length;
  const rejectedCount = rows.filter((r) => r.status === "REJECTED").length;
  const recommendedChangeCount = rows.filter(
    (r) => r.status === "RECOMMEND_CHANGE"
  ).length;
  const totalRequired = minApprovers ?? rows.length;

  const myRow =
    isCurrentUserApprover && currentUserId
      ? rows.find((r) => r.userId === currentUserId)
      : undefined;

  const collapsedSummary =
    flowStatus === "COMPLETED"
      ? "All sign-offs collected"
      : [
          approvedCount && `${approvedCount} approved`,
          rejectedCount && `${rejectedCount} rejected`,
          recommendedChangeCount &&
            `${recommendedChangeCount} change recommended`,
        ]
          .filter(Boolean)
          .join(", ") || `0 of ${totalRequired} sign-offs collected`;

  function resetRecommendChangeForm() {
    setActionMode("none");
    setComment("");
    setProposedChangeRows([{ field: "", value: "" }]);
    setProposedInputMode(recommendationInputMode);
  }

  function handleSubmitRecommendChange() {
    if (!myRow || !currentUserId) return;
    const enteredValue = proposedChangeRows[0]?.value.trim();
    const suggestedChanges = enteredValue
      ? { inputMode: proposedInputMode, enteredValue: Number(enteredValue) }
      : {};
    submitRecommendChange(
      {
        signOffId: myRow.id,
        payload: {
          comment,
          proposedChanges: Object.keys(suggestedChanges).length
            ? suggestedChanges
            : undefined,
        },
      },
      {
        onSuccess: () => {
          resetRecommendChangeForm();
          toast("Recommendation submitted successfully!", {
            type: "success",
            autoClose: 2000,
          });
        },
        onError: (err: any) => {
          toast(err?.message || "Failed to submit recommendation", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  }

  function handleSignOff(decision: SignOffDecision) {
    if (!myRow || !currentUserId) return;
    submitSignOff(
      {
        signOffId: myRow.id,
        payload: { decision },
      },
      {
        onSuccess: () => {
          setComment("");
          toast(
            decision === SignOffDecision.APPROVED
              ? "Grant approved successfully!"
              : "Grant rejected successfully!",
            { type: "success", autoClose: 2000 }
          );
        },
        onError: (err: any) => {
          toast(err?.message || "Failed to submit sign-off", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  }

  function handleOverride() {
    if (!overrideRowId) return;
    overrideSignOff(
      { signOffId: overrideRowId, comment: overrideComment || undefined },
      {
        onSuccess: () => {
          setOverrideRowId(null);
          setOverrideComment("");
        },
        onError: (err: any) => {
          toast(err?.message || "Failed to override sign-off", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  }

  return (
    <div className="border border-borderColor rounded-lg shadow-box bg-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <HStack className="items-center gap-2 flex-1 min-w-0">
          <span className="font-medium text-sm text-primaryText">
            Approvals
          </span>
          {!expanded && !fetchingRows && (
            <>
              <span className="text-xs text-gray-light">·</span>
              <span
                className={`text-xs ${
                  flowStatus === "COMPLETED"
                    ? "text-green-600"
                    : flowStatus === "REJECTED"
                    ? "text-red-500"
                    : flowStatus === "RECOMMEND_CHANGE"
                    ? "text-orange-600"
                    : "text-secondaryText"
                }`}
              >
                {collapsedSummary}
              </span>
            </>
          )}
        </HStack>
        <button
          className="text-gray-light hover:text-primaryText ml-2 flex-shrink-0"
          onClick={() => setExpanded((p) => !p)}
        >
          <Icon
            icon={expanded ? "ri:arrow-up-s-line" : "ri:arrow-down-s-line"}
            height={20}
          />
        </button>
      </div>

      {expanded && (
        <VStack className="mt-4 gap-3">
          {fetchingRows ? (
            <p className="text-sm text-gray-light">Loading approval status…</p>
          ) : (
            <>
              {/* Summary row */}
              <HStack className="items-center justify-between">
                <span className="text-xs text-secondaryText">
                  {isSequential
                    ? "Sequential approval"
                    : "Parallel approval (any order)"}
                </span>
              </HStack>

              {/* Approver rows */}
              <VStack className="gap-2">
                {rows.map((row) => {
                  const cfg = STATUS_CONFIG[row.status];
                  const isMe = !!currentUserId && row.userId === currentUserId;
                  const planApprover = (
                    planApprovalConfig?.approvers ?? []
                  ).find((a) => a.userId === row.userId);
                  const canOverride =
                    userIsAdmin &&
                    !row.id.startsWith("plan-") &&
                    (row.status === "PENDING" ||
                      row.status === "RECOMMEND_CHANGE");
                  const isMyTurn =
                    !isSequential ||
                    !rows.some(
                      (r) => r.order < row.order && r.status === "PENDING"
                    );
                  const isActiveForMe =
                    isMe && row.status === "PENDING" && isMyTurn;
                  return (
                    <div
                      key={row.id}
                      className="rounded-lg border border-borderColor bg-white p-3"
                    >
                      <HStack className="items-start justify-between gap-2">
                        <HStack className="items-start gap-2 flex-1 min-w-0">
                          <span className="text-xs font-semibold text-gray-400 w-4 shrink-0 mt-0.5">
                            {row.order}.
                          </span>
                          <VStack className="gap-0.5 min-w-0">
                            <HStack className="items-center gap-2">
                              <span className="text-sm font-medium text-primaryText">
                                {row.approverName}
                              </span>
                              {isMe && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-600">
                                  YOU
                                </span>
                              )}
                            </HStack>
                            {planApprover?.expiryDays &&
                              row.status === "PENDING" && (
                                <span className="text-[11px] text-gray-500 mt-0.5">
                                  Expires in {planApprover.expiryDays} days
                                  {planApprover.onExpiryAction && (
                                    <>
                                      {" "}
                                      • Auto{" "}
                                      {planApprover.onExpiryAction ===
                                      "AUTO_APPROVE"
                                        ? "approve"
                                        : planApprover.onExpiryAction ===
                                          "AUTO_REJECT"
                                        ? "reject"
                                        : "escalate"}
                                    </>
                                  )}
                                </span>
                              )}
                            {row.comment && (
                              <p className="text-xs text-secondaryText italic mt-0.5">
                                &ldquo;{row.comment}&rdquo;
                              </p>
                            )}
                          </VStack>
                        </HStack>
                        <VStack className="items-end gap-1 shrink-0">
                          <span
                            className={`text-[11px] font-medium px-2 py-0.5 rounded-sm ${cfg.className}`}
                          >
                            {cfg.label}
                          </span>
                        </VStack>
                      </HStack>

                      {/* Sign-off actions — inline in this approver's own row */}
                      {isActiveForMe && actionMode === "none" && (
                        <HStack className="justify-between gap-2 mt-3 pt-3 border-t border-borderColor">
                          <button
                            className="text-xs font-medium text-orange-600 hover:text-orange-700 px-2 py-1 underline"
                            onClick={() => setActionMode("recommend_change")}
                          >
                            Recommend Change
                          </button>
                          <HStack className="gap-2">
                            <ButtonPrimary1
                              loading={signOffLoading}
                              onClick={() =>
                                handleSignOff(SignOffDecision.REJECTED)
                              }
                            >
                              Reject
                            </ButtonPrimary1>
                            <ButtonPrimary
                              loading={signOffLoading}
                              onClick={() =>
                                handleSignOff(SignOffDecision.APPROVED)
                              }
                            >
                              Approve
                            </ButtonPrimary>
                          </HStack>
                        </HStack>
                      )}

                      {row.status === "RECOMMEND_CHANGE" &&
                        userIsAdmin &&
                        recommendationByApprovalId.has(row.id) && (
                          <div className="mt-3 pt-3 border-t border-borderColor">
                            <RecommendationInlineActions
                              grantId={grantId}
                              recommendation={
                                recommendationByApprovalId.get(row.id)!
                              }
                              onNavigate={onClose}
                            />
                          </div>
                        )}

                      {isActiveForMe && actionMode === "recommend_change" && (
                        <div className="mt-3 pt-3 border-t border-borderColor">
                          <HStack className="items-center gap-1 mt-3 mb-1">
                            <p className="text-xs font-medium text-gray-700">
                              {proposedInputMode === "VALUE"
                                ? "Recommended value"
                                : PROPOSED_CHANGE_FIELDS[0].label}{" "}
                              (optional)
                            </p>
                            <Tooltip
                              text="Suggest a revised option count or value. The recommendation is converted using the current valuation, and the admin will review both values before editing the grant."
                              _className="w-72"
                            >
                              <Icon
                                icon="material-symbols:info-outline-rounded"
                                height={14}
                                width={14}
                                className="text-slate-dark cursor-pointer"
                              />
                            </Tooltip>
                          </HStack>
                          <div className="flex gap-2">
                            <select
                              aria-label="Recommendation type"
                              value={proposedInputMode}
                              onChange={(e) => {
                                setProposedInputMode(
                                  e.target.value as "OPTIONS" | "VALUE"
                                );
                                setProposedChangeRows([
                                  { field: "", value: "" },
                                ]);
                              }}
                              className="rounded border border-gray-200 bg-white px-2 text-sm text-gray-700 outline-none focus:border-orange-400"
                            >
                              <option value="OPTIONS">Options</option>
                              <option value="VALUE">Value</option>
                            </select>
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              placeholder={
                                proposedInputMode === "VALUE"
                                  ? "Proposed value"
                                  : "Proposed no. of options"
                              }
                              value={proposedChangeRows[0]?.value ?? ""}
                              onChange={(e) =>
                                setProposedChangeRows([
                                  {
                                    field: "enteredValue",
                                    value: e.target.value,
                                  },
                                ])
                              }
                            />
                          </div>

                          <p className="text-xs font-medium text-gray-700 mt-3 mb-1">
                            Notes (optional)
                          </p>
                          <TextArea
                            placeholder="Add any context for the admin reviewing this recommendation"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                          />

                          <HStack className="justify-between gap-2 mt-3">
                            <ButtonPrimary1 onClick={resetRecommendChangeForm}>
                              Cancel
                            </ButtonPrimary1>
                            <ButtonPrimary
                              loading={recommendLoading}
                              onClick={handleSubmitRecommendChange}
                            >
                              Submit Recommendation
                            </ButtonPrimary>
                          </HStack>
                        </div>
                      )}
                    </div>
                  );
                })}
              </VStack>
            </>
          )}
        </VStack>
      )}
    </div>
  );
}
